import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core'
import { Router } from '@angular/router'
import { PaywallContentType, PaywallInputs } from './paywall.interface'
import { AuthService } from '../../singleton-services/auth/auth.service'
import { map, shareReplay, switchMap } from 'rxjs/operators'
import { SubscriptionService } from 'src/app/shared/services/subscription.service'
import { Observable, of } from 'rxjs'

@Component({
  selector: 'flo-paywall',
  templateUrl: './paywall.component.html',
  styleUrls: ['./paywall.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaywallComponent implements PaywallInputs {
  @HostBinding('class.wrapped')
  @Input()
  public wrapped = false

  @HostBinding('class.overlayed')
  @Input()
  public overlayed = false

  @Input()
  public loginRoute = '/login'

  @Input()
  public loggedIn: boolean

  /** When provided, replaces the default prompt text for the paywall. */
  @Input()
  public promptOverride: string

  /** When provided, replaces the text on the primary join button. */
  @Input()
  public joinButtonTextOverride: string

  /** When provided, replaces the text on the login button. */
  @Input()
  public loginButtonTextOverride: string

  /** When provided, customizes the paywall to the content type. */
  @Input()
  public contentType: PaywallContentType = 'content'

  /** Set the URI to redirect to after login or joining */
  @Input()
  public redirect: string

  /** The route the user will be directed to when clicking the join button. */
  @Input()
  public joinRoute = '/signup'

  public defaultMessage = `Unlock live events and more with a subscription!`

  constructor(router: Router, private authService: AuthService, private subscriptionService: SubscriptionService) {
    this.redirect = router.url
  }

  public readonly hasPausedSubscription$: Observable<boolean> = this.authService.userIdentity$.pipe(
    switchMap(user => (user ? this.subscriptionService.userHasPausedSubscription(user.id) : of(false))),
    shareReplay(1)
  )

  public pausedRoute = '/account/subscriptions/'

  public combinedModes$ = this.hasPausedSubscription$.pipe(
    map(hasPausedSubscription => ({
      promptMode: hasPausedSubscription ? 'paused' : this.promptOverride ? 'override' : 'default',
      joinCtaMode: hasPausedSubscription ? 'paused' : this.joinButtonTextOverride ? 'override' : 'default'
    }))
  )

  public get headerClasses(): Record<string, boolean> {
    return {
      header: true,
      'paywall-header': true,
      'paywall-header--light': this.contentType === 'video',
      'paywall-header--dark': this.contentType !== 'video'
    }
  }
}
